import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, Upload, Trash2, FileText, ArrowLeft, FolderOpen } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';
import {
  getStore,
  getStoreDocuments,
  uploadDocument,
  deleteDocument,
  FileSearchStore,
  FileSearchDocument,
} from '../../services/FileSearchStores';

const StoreDetailsPage: React.FC = () => {
  const { storeId } = useParams<{ storeId: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [store, setStore] = useState<FileSearchStore | null>(null);
  const [documents, setDocuments] = useState<FileSearchDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>({});

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [docToDelete, setDocToDelete] = useState<FileSearchDocument | null>(null);
  const [deleting, setDeleting] = useState(false);

  const formatFileSize = (bytes?: string): string => {
    if (!bytes) return 'Unknown';
    const size = parseInt(bytes, 10);
    if (size === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(size) / Math.log(k));
    return Math.round((size / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  const columns: ColumnDef<FileSearchDocument>[] = [
    {
      accessorKey: 'displayName',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 hover:bg-transparent"
          >
            Filename
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{row.getValue('displayName')}</span>
        </div>
      ),
    },
    {
      accessorKey: 'sizeBytes',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 hover:bg-transparent"
          >
            File Size
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const sizeBytes = row.getValue('sizeBytes') as string | undefined;
        return formatFileSize(sizeBytes);
      },
    },
    {
      accessorKey: 'state',
      header: 'Status',
      cell: ({ row }) => {
        const state = row.getValue('state') as string | undefined;
        if (!state) return <Badge variant="secondary">Unknown</Badge>;

        const variant =
          state === 'STATE_ACTIVE'
            ? 'success'
            : state === 'STATE_PROCESSING'
              ? 'warning'
              : 'destructive';
        return <Badge variant={variant}>{state}</Badge>;
      },
    },
    {
      accessorKey: 'createdAt',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => column.toggleSorting(column.getIsSorted() === 'asc')}
            className="h-auto p-0 hover:bg-transparent"
          >
            Uploaded At
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const createdAt = row.getValue('createdAt') as string | undefined;
        if (!createdAt) return 'N/A';
        const date = new Date(createdAt);
        return date.toLocaleString();
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const doc = row.original;
        return (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleDeleteClick(doc)}
            className="h-auto p-0 hover:bg-transparent"
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        );
      },
    },
  ];

  const table = useReactTable({
    data: documents,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
    },
  });

  const fetchStoreAndDocuments = useCallback(async () => {
    if (!storeId) return;

    setLoading(true);
    try {
      const [storeData, documentsData] = await Promise.all([
        getStore(storeId),
        getStoreDocuments(storeId),
      ]);
      setStore(storeData);
      setDocuments(documentsData.page);
    } catch (error) {
      toast.error('Error loading store details');
      console.error('Error fetching store details:', error);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    if (storeId) {
      fetchStoreAndDocuments();
    }
  }, [storeId, fetchStoreAndDocuments]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !storeId) return;

    setUploading(true);
    setUploadStatus('Starting upload...');

    try {
      await uploadDocument(storeId, file, (status) => {
        setUploadStatus(status);
      });
      toast.success(`File "${file.name}" uploaded successfully`);
      await fetchStoreAndDocuments();
    } catch (error) {
      toast.error('Error uploading file');
      console.error('Upload error:', error);
    } finally {
      setUploading(false);
      setUploadStatus('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteClick = (doc: FileSearchDocument) => {
    setDocToDelete(doc);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!docToDelete || !storeId) return;

    setDeleting(true);
    try {
      await deleteDocument(storeId, docToDelete.id);
      toast.success(`File "${docToDelete.displayName}" deleted successfully`);
      await fetchStoreAndDocuments();
    } catch (error) {
      toast.error('Error deleting file');
      console.error('Delete error:', error);
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setDocToDelete(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!store) {
    return (
      <div className="p-8">
        <p className="text-destructive">Store not found</p>
      </div>
    );
  }

  return (
    <div className="w-full space-y-4 p-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
        <button
          onClick={() => navigate('/file-search-stores')}
          className="hover:text-foreground transition-colors"
        >
          File Search Stores
        </button>
        <span>/</span>
        <span className="text-foreground">{store.name}</span>
      </div>

      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">{store.name}</h1>
          {store.description && (
            <p className="text-muted-foreground mt-2">{store.description}</p>
          )}
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/file-search-stores')}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Stores
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            style={{ display: 'none' }}
            accept=".pdf,.doc,.docx,.txt,.md"
          />
          <Button onClick={handleUploadClick} disabled={uploading}>
            <Upload className="mr-2 h-4 w-4" />
            {uploading ? uploadStatus || 'Uploading...' : 'Upload Document'}
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="flex items-center py-4">
        <Input
          placeholder="Filter files..."
          value={(table.getColumn('displayName')?.getFilterValue() as string) ?? ''}
          onChange={(event) =>
            table.getColumn('displayName')?.setFilterValue(event.target.value)
          }
          className="max-w-sm"
        />
      </div>

      {/* Table */}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  Loading...
                </TableCell>
              </TableRow>
            ) : table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  <div className="flex flex-col items-center justify-center space-y-4">
                    <FolderOpen className="h-16 w-16 text-muted-foreground" />
                    <div className="text-center">
                      <h3 className="text-lg font-medium">No documents in this store</h3>
                      <p className="text-muted-foreground">
                        Upload documents to make them searchable in chat
                      </p>
                    </div>
                    <Button onClick={handleUploadClick}>
                      <Upload className="mr-2 h-4 w-4" />
                      Upload First Document
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <div className="flex items-center justify-end py-2">
        <div className="text-sm text-muted-foreground">
          {documents.length} document(s) loaded
        </div>
      </div>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Document</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete "{docToDelete?.displayName}"? This action
              cannot be undone and will remove the file from Gemini File Search.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
              disabled={deleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default StoreDetailsPage;
